import React, { useState } from 'react';
import emailjs from '@emailjs/browser';
import { saveResetCode, verifyResetCode, clearResetCode } from '../supabaseClient';
import { hashPassword, isHashed } from '../utils/cryptoUtils';

/* ── Styles ── */
const S = {
  wrap:     { minHeight:'100vh', display:'flex', alignItems:'center', justifyContent:'center', background:'#5a5a5a', padding:16 },
  card:     { width:'100%', maxWidth:400, borderRadius:8, overflow:'hidden', boxShadow:'0 16px 56px rgba(0,0,0,.5)' },
  head:     { background:'#e8e8e8', display:'flex', alignItems:'center', gap:16, padding:'18px 24px', borderBottom:'3px solid #CC0000' },
  logo:     { width:56, height:56, borderRadius:6, objectFit:'contain', background:'transparent', padding:0, flexShrink:0 },
  headText: { fontWeight:900, fontSize:14, color:'#1a1a1a', textTransform:'uppercase', lineHeight:1.35, letterSpacing:.3 },
  headSub:  { fontSize:11, fontWeight:400, color:'#666', marginTop:2 },
  body:     { background:'#6e6e6e', padding:'22px 24px 28px' },
  infoBox:  { background:'rgba(0,0,0,.18)', borderRadius:6, padding:'14px 18px', textAlign:'center', marginBottom:18 },
  lockIcon: { fontSize:26, display:'block', marginBottom:6 },
  infoText: { color:'#ddd', fontSize:12, marginBottom:3 },
  infoTitle:{ color:'#ff8888', fontWeight:700, fontSize:13 },
  label:    { display:'block', fontSize:11, fontWeight:700, textTransform:'uppercase', letterSpacing:.4, color:'rgba(255,255,255,.6)', marginBottom:5 },
  inputWrap:{ position:'relative', marginBottom:12 },
  input:    { width:'100%', padding:'11px 14px', background:'#fff', border:'none', borderRadius:4, fontSize:14, color:'#222', outline:'none', boxSizing:'border-box' },
  eyeBtn:   { position:'absolute', right:10, top:'50%', transform:'translateY(-50%)', background:'none', border:'none', cursor:'pointer', color:'#888', fontSize:16 },
  btn:      { width:'100%', padding:'13px', background:'#CC0000', color:'#fff', border:'none', borderRadius:4, fontSize:15, fontWeight:700, cursor:'pointer', letterSpacing:.3, transition:'filter .15s, transform .1s', marginTop:4 },
  error:    { background:'rgba(255,60,60,.18)', border:'1px solid rgba(255,80,80,.35)', borderRadius:4, padding:'8px 12px', color:'#ffbbbb', fontSize:12, marginBottom:10 },
  hint:     { textAlign:'center', marginTop:14, color:'rgba(255,255,255,.3)', fontSize:11 },
};

const logoSrc = (process.env.PUBLIC_URL || '') + '/fo-logo.png';

const EJS_SERVICE  = process.env.REACT_APP_EMAILJS_SERVICE_ID  || '';
const EJS_TEMPLATE = process.env.REACT_APP_EMAILJS_TEMPLATE_ID || '';
const EJS_KEY      = process.env.REACT_APP_EMAILJS_PUBLIC_KEY  || '';

/* ── Main login form ── */
function LoginForm({ users, saveUser, onSuccess, onForgot, remoteLoading }) {
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError]       = useState('');
  const [showPwd, setShowPwd]   = useState(false);

  const handleSubmit = async () => {
    const u = users.find(x => x.username === username.trim());
    if (!u) { setError('Identifiant ou mot de passe incorrect.'); setPassword(''); return; }
    const hashed = await hashPassword(password);
    if (isHashed(u.password)) {
      if (u.password !== hashed) { setError('Identifiant ou mot de passe incorrect.'); setPassword(''); return; }
    } else {
      if (u.password !== password) { setError('Identifiant ou mot de passe incorrect.'); setPassword(''); return; }
      saveUser({ ...u, password: hashed });
    }
    onSuccess(u);
  };

  return (
    <div style={S.body}>
      <div style={S.infoBox}>
        <span style={S.lockIcon}>🔒</span>
        <div style={S.infoText}>Utilisez vos identifiants pour accéder à</div>
        <div style={S.infoTitle}>Gestion Syndicale SDASSP FO Réunion</div>
      </div>

      <div style={S.inputWrap}>
        <label style={S.label}>Identifiant</label>
        <input style={S.input} value={username} autoFocus
          onChange={e => { setUsername(e.target.value); setError(''); }}
          onKeyDown={e => e.key === 'Enter' && handleSubmit()}
          placeholder="Votre identifiant" />
      </div>

      <div style={S.inputWrap}>
        <label style={S.label}>Mot de passe</label>
        <input style={S.input} type={showPwd ? 'text' : 'password'} value={password}
          onChange={e => { setPassword(e.target.value); setError(''); }}
          onKeyDown={e => e.key === 'Enter' && handleSubmit()}
          placeholder="••••••••" />
        <button type="button" style={S.eyeBtn} onClick={() => setShowPwd(v => !v)}
          title={showPwd ? 'Masquer' : 'Afficher'}>
          {showPwd ? '🙈' : '👁'}
        </button>
      </div>

      {error && <div style={S.error}>⚠ {error}</div>}

      {remoteLoading && (
        <div style={{ background:'rgba(0,0,0,.25)', borderRadius:4, padding:'8px 12px', color:'rgba(255,255,255,.6)', fontSize:12, marginBottom:10, textAlign:'center' }}>
          ⏳ Connexion au serveur en cours...
        </div>
      )}
      <button style={{ ...S.btn, opacity: remoteLoading ? 0.5 : 1, cursor: remoteLoading ? 'not-allowed' : 'pointer' }}
        onClick={remoteLoading ? undefined : handleSubmit}
        onMouseEnter={e => { if (!remoteLoading) { e.currentTarget.style.filter='brightness(1.1)'; e.currentTarget.style.transform='translateY(-1px)'; } }}
        onMouseLeave={e => { e.currentTarget.style.filter=''; e.currentTarget.style.transform=''; }}
        onMouseDown={e => { if (!remoteLoading) e.currentTarget.style.transform='scale(.98)'; }}
        onMouseUp={e => { if (!remoteLoading) e.currentTarget.style.transform='translateY(-1px)'; }}
        title={remoteLoading ? 'Chargement des données en cours, veuillez patienter...' : "Se connecter à l'application"}>
        {remoteLoading ? '⏳ Chargement...' : 'Se connecter'}
      </button>
      <div style={{ textAlign:'center', marginTop:12 }}>
        <button type="button" onClick={onForgot}
          style={{ background:'none', border:'none', color:'rgba(255,255,255,.5)', fontSize:12, cursor:'pointer', textDecoration:'underline' }}>
          Mot de passe oublié ?
        </button>
      </div>
      <div style={S.hint}>SDASSP FO Réunion 974 — Accès sécurisé</div>
    </div>
  );
}

/* ── Forgot password flow ── */
function ForgotPasswordForm({ users, saveUser, onBack }) {
  const [step, setStep]       = useState(1); // 1=email, 2=code, 3=newpwd
  const [email, setEmail]     = useState('');
  const [code, setCode]       = useState('');
  const [userId, setUserId]   = useState(null);
  const [pwd, setPwd]         = useState('');
  const [confirm, setConfirm] = useState('');
  const [showPwd, setShowPwd] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError]     = useState('');
  const [ok, setOk]           = useState('');

  const handleSendCode = async () => {
    const em = email.trim().toLowerCase();
    if (!em) { setError('Saisissez votre adresse e-mail.'); return; }
    const user = users.find(u => (u.email || '').toLowerCase() === em);
    if (!user) { setError('Aucun compte associé à cet e-mail.'); return; }
    if (!EJS_SERVICE || !EJS_TEMPLATE || !EJS_KEY) {
      setError('Service e-mail non configuré. Contactez l’administrateur.');
      return;
    }
    setLoading(true); setError('');
    const generatedCode = String(Math.floor(100000 + Math.random() * 900000));
    await saveResetCode(em, generatedCode, user.id);
    try {
      await emailjs.send(EJS_SERVICE, EJS_TEMPLATE, {
        to_email: em,
        user_name: user.username,
        reset_code: generatedCode,
      }, EJS_KEY);
      setStep(2);
      setOk('Code envoyé ! Vérifiez votre boîte mail (valable 15 minutes).');
    } catch {
      setError('Erreur d’envoi de l’e-mail. Vérifiez la configuration EmailJS.');
    }
    setLoading(false);
  };

  const handleVerifyCode = async () => {
    if (code.length !== 6) { setError('Le code fait 6 chiffres.'); return; }
    setLoading(true); setError('');
    const uid = await verifyResetCode(email.trim().toLowerCase(), code.trim());
    if (!uid) { setError('Code incorrect ou expiré.'); setLoading(false); return; }
    setUserId(uid);
    setStep(3);
    setOk('');
    setLoading(false);
  };

  const handleNewPwd = async () => {
    if (pwd.length < 10) { setError('Minimum 10 caractères.'); return; }
    if (pwd !== confirm) { setError('Les mots de passe ne correspondent pas.'); return; }
    const user = users.find(u => u.id === userId);
    if (!user) { setError('Utilisateur introuvable.'); return; }
    const hashed = await hashPassword(pwd);
    saveUser({ ...user, password: hashed, mustChangePassword: false });
    await clearResetCode(email.trim().toLowerCase());
    setOk('✅ Mot de passe modifié ! Vous pouvez vous connecter.');
    setTimeout(() => onBack(), 2500);
  };

  return (
    <div style={S.body}>
      <div style={{ ...S.infoBox, background:'rgba(0,100,200,.2)' }}>
        <span style={S.lockIcon}>📧</span>
        <div style={{ color:'#bbddff', fontWeight:700, fontSize:13 }}>
          {step === 1 && 'Réinitialisation du mot de passe'}
          {step === 2 && 'Saisissez le code reçu'}
          {step === 3 && 'Nouveau mot de passe'}
        </div>
      </div>

      {error && <div style={S.error}>⚠ {error}</div>}
      {ok && <div style={{ ...S.error, background:'rgba(22,163,74,.18)', border:'1px solid rgba(22,163,74,.35)', color:'#86efac' }}>{ok}</div>}

      {step === 1 && (
        <>
          <div style={S.inputWrap}>
            <label style={S.label}>Votre adresse e-mail</label>
            <input style={S.input} type="email" value={email} autoFocus
              onChange={e => { setEmail(e.target.value); setError(''); }}
              onKeyDown={e => e.key === 'Enter' && handleSendCode()}
              placeholder="prenom.nom@exemple.fr" />
          </div>
          <button style={S.btn} onClick={handleSendCode} disabled={loading}>
            {loading ? 'Envoi...' : 'Envoyer le code'}
          </button>
        </>
      )}

      {step === 2 && (
        <>
          <div style={S.inputWrap}>
            <label style={S.label}>Code à 6 chiffres</label>
            <input style={{ ...S.input, letterSpacing:8, fontSize:20, textAlign:'center' }}
              type="text" inputMode="numeric" maxLength={6} value={code}
              onChange={e => { setCode(e.target.value.replace(/\D/g, '')); setError(''); }}
              onKeyDown={e => e.key === 'Enter' && handleVerifyCode()}
              placeholder="000000" autoFocus />
          </div>
          <button style={S.btn} onClick={handleVerifyCode} disabled={loading}>
            {loading ? 'Vérification...' : 'Valider le code'}
          </button>
        </>
      )}

      {step === 3 && (
        <>
          <div style={S.inputWrap}>
            <label style={S.label}>Nouveau mot de passe</label>
            <input style={S.input} type={showPwd ? 'text' : 'password'} value={pwd} autoFocus
              onChange={e => { setPwd(e.target.value); setError(''); }}
              placeholder="Minimum 10 caractères" />
            <button type="button" style={S.eyeBtn} onClick={() => setShowPwd(v => !v)}>
              {showPwd ? '🙈' : '👁'}
            </button>
          </div>
          <div style={S.inputWrap}>
            <label style={S.label}>Confirmer</label>
            <input style={S.input} type={showPwd ? 'text' : 'password'} value={confirm}
              onChange={e => { setConfirm(e.target.value); setError(''); }}
              onKeyDown={e => e.key === 'Enter' && handleNewPwd()}
              placeholder="Confirmez le mot de passe" />
          </div>
          <button style={S.btn} onClick={handleNewPwd}>✔ Enregistrer</button>
        </>
      )}

      <div style={{ textAlign:'center', marginTop:12 }}>
        <button type="button" onClick={onBack}
          style={{ background:'none', border:'none', color:'rgba(255,255,255,.5)', fontSize:12, cursor:'pointer', textDecoration:'underline' }}>
          ← Retour à la connexion
        </button>
      </div>
    </div>
  );
}

/* ── Change password form (first login) ── */
function ChangePwdForm({ user, saveUser, onSuccess }) {
  const [pwd, setPwd]       = useState('');
  const [confirm, setConfirm] = useState('');
  const [showPwd, setShowPwd] = useState(false);
  const [error, setError]   = useState('');

  const handleSubmit = async () => {
    if (pwd.length < 10) { setError('Mot de passe trop court (minimum 10 caractères).'); return; }
    if (pwd !== confirm) { setError('Les mots de passe ne correspondent pas.'); return; }
    const hashed = await hashPassword(pwd);
    const updated = { ...user, password: hashed, mustChangePassword: false };
    saveUser(updated);
    onSuccess(updated);
  };

  return (
    <div style={S.body}>
      <div style={{ ...S.infoBox, background:'rgba(204,0,0,.25)' }}>
        <span style={S.lockIcon}>🔑</span>
        <div style={{ color:'#ffdddd', fontSize:12, marginBottom:3 }}>Première connexion — sécurisez votre compte</div>
        <div style={{ color:'#ffaaaa', fontWeight:700, fontSize:13 }}>Choisissez votre mot de passe personnel</div>
      </div>

      <div style={{ color:'rgba(255,255,255,.7)', fontSize:12, marginBottom:14 }}>
        Bonjour <strong style={{ color:'#fff' }}>{user.roleTitle}</strong>,<br/>
        Ce compte a été créé par un administrateur. Vous devez définir votre propre mot de passe avant de continuer.
      </div>

      <div style={S.inputWrap}>
        <label style={S.label}>Nouveau mot de passe</label>
        <input style={S.input} type={showPwd ? 'text' : 'password'} value={pwd} autoFocus
          onChange={e => { setPwd(e.target.value); setError(''); }}
          onKeyDown={e => e.key === 'Enter' && handleSubmit()}
          placeholder="Minimum 10 caractères" />
        <button type="button" style={S.eyeBtn} onClick={() => setShowPwd(v => !v)}
          title={showPwd ? 'Masquer' : 'Afficher'}>{showPwd ? '🙈' : '👁'}</button>
      </div>

      <div style={S.inputWrap}>
        <label style={S.label}>Confirmer le mot de passe</label>
        <input style={S.input} type={showPwd ? 'text' : 'password'} value={confirm}
          onChange={e => { setConfirm(e.target.value); setError(''); }}
          onKeyDown={e => e.key === 'Enter' && handleSubmit()}
          placeholder="Répétez le mot de passe" />
      </div>

      {error && <div style={S.error}>⚠ {error}</div>}

      <button style={S.btn} onClick={handleSubmit}
        onMouseEnter={e => { e.currentTarget.style.filter='brightness(1.1)'; e.currentTarget.style.transform='translateY(-1px)'; }}
        onMouseLeave={e => { e.currentTarget.style.filter=''; e.currentTarget.style.transform=''; }}
        onMouseDown={e => e.currentTarget.style.transform='scale(.98)'}
        onMouseUp={e => e.currentTarget.style.transform='translateY(-1px)'}>
        ✔ Enregistrer et accéder
      </button>
    </div>
  );
}

/* ── Shell ── */
export default function PinLogin({ users, saveUser, onLogin, remoteLoading }) {
  const [pendingUser, setPendingUser] = useState(null);
  const [showForgot, setShowForgot]   = useState(false);

  const handleSuccess = (user) => {
    if (user.mustChangePassword) {
      setPendingUser(user);
    } else {
      onLogin(user.role, user.roleTitle, user.isAdmin || false, user.username);
    }
  };

  const handlePasswordChanged = (user) => {
    onLogin(user.role, user.roleTitle, user.isAdmin || false, user.username);
  };

  return (
    <div style={S.wrap}>
      <div style={S.card}>
        <div style={S.head}>
          <img src={logoSrc} alt="SDASSP FO Réunion" style={S.logo}
            onError={e => { e.target.style.display='none'; }} />
          <div>
            <div style={S.headText}>SERVICE<br/>D'AUTHENTIFICATION</div>
            <div style={S.headSub}>SDASSP FO Réunion</div>
          </div>
        </div>

        {showForgot
          ? <ForgotPasswordForm users={users} saveUser={saveUser} onBack={() => setShowForgot(false)} />
          : pendingUser
            ? <ChangePwdForm user={pendingUser} saveUser={saveUser} onSuccess={handlePasswordChanged} />
            : <LoginForm users={users} saveUser={saveUser} onSuccess={handleSuccess} onForgot={() => setShowForgot(true)} remoteLoading={remoteLoading} />
        }
      </div>
    </div>
  );
}
